/**
 * Shared value types for the action modal form.
 *
 * The entire modal uses a single generated `useForm` with one
 * `type: 'custom'` field whose value is the full `ActionFormValues`
 * object. Sub-tab components read via `form.values.form` and write
 * via `form.setFieldValue('form.<key>', next)`.
 */

import type { FormContext } from '@/shared/lib/forms'

import type { PredicateNode } from '@/admin/components/condition-builder/types'
import type {
  WorkflowActor,
  WorkflowApplicability,
  WorkflowOverwriteSpec,
  WorkflowSideEffectEntry,
  WorkflowTrigger,
} from '@/admin/hooks/use-workflow-definitions'

export interface ActionFormValues {
  key: string
  label: string
  icon: string
  applicability: WorkflowApplicability
  actor: WorkflowActor
  trigger: WorkflowTrigger
  source_state: string | null
  target_state: string | null
  order: number
  priority: number
  condition: PredicateNode | null
  side_effects: WorkflowSideEffectEntry[]
  ui_hints: Record<string, unknown>
  // Rewrite §5.5 - attribute UUIDs this action requires the user to
  // provide when capturing values.
  required_attrs: string[]
  // Rewrite §: entity-column overwrites applied when the action fires.
  overwrites: Record<string, WorkflowOverwriteSpec>
}

export type ActionFormContext = FormContext

export function readForm(form: ActionFormContext): ActionFormValues {
  return (form.values.form ?? {}) as ActionFormValues
}

export function setFormField<K extends keyof ActionFormValues>(
  form: ActionFormContext,
  key: K,
  value: ActionFormValues[K],
): void {
  form.setFieldValue(`form.${key as string}`, value)
}
